import {
  DetailsChecklist,
  DetailsChecklistProps,
  RollupQuestionProvider,
  TraitTable,
  TraitTableProps,
} from '@cibo/landmanager'
import { useFormFactor } from '@cibo/ui'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import { Button, Drawer, Grid, Paper, Stack, Typography } from '@mui/material'
import { propEq } from 'ramda'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useFieldProgramTraitReasonsColumn, useProgram } from '../../'
import { AddInformationNavButtons, AddInformationNavButtonsProps } from './NavButtons'
import { MultiStepEditorProps } from './types'

type AddInformationProps = {
  NavButtonsOverride?: typeof AddInformationNavButtons
  growerName?: string
} & MultiStepEditorProps &
  Pick<TraitTableProps, 'programIds' | 'benchmark'> &
  Pick<AddInformationNavButtonsProps, 'onContinue' | 'onFinishLater'> &
  Pick<DetailsChecklistProps, 'hideLegend'>

export const AddInformation = ({
  isLoadingData,
  years,
  fields,
  hideLegend,
  focusTraitId,
  traitsLoading,
  traitsError,
  traitStatuses,
  detailRequirements,
  onContinue,
  onFinishLater,
  setFocusTraitId,
  ToolbarButtons,
  NavButtonsOverride,
  growerName,
  ...otherProps
}: AddInformationProps) => {
  const { t } = useTranslation('AddInformation')
  const { isMobile } = useFormFactor()
  const [drawerOpen, setDrawerOpen] = useState(false)
  const { t: traitIdT } = useTranslation('@cibo/landmanager/components')
  const NavButtonsComponent = !!NavButtonsOverride ? NavButtonsOverride : AddInformationNavButtons

  const traitRequirements = detailRequirements?.filter(propEq('traitId', focusTraitId))

  const setFocusTraitIdAndOpenDrawer = (args: any) => {
    if (isMobile) {
      setFocusTraitId(args)
      setDrawerOpen(true)
    } else {
      setFocusTraitId(args)
    }
  }

  const statusColumn = useFieldProgramTraitReasonsColumn({
    programIds: otherProps.programIds,
    traitId: focusTraitId,
  })

  const program = useProgram({ programId: otherProps.programIds?.[0] })
  const filterInputs = program.data?.prgDetails?.find(detail => detail.traitId === focusTraitId)

  const traitEditor = (
    <Stack spacing={3}>
      {!!focusTraitId && traitRequirements && (
        <Paper sx={{ p: 2 }}>
          <TraitTable
            disableRollup={(fields?.length || 0) < 1}
            detailRequirements={traitRequirements}
            fieldModels={fields || []}
            traitId={focusTraitId}
            ToolbarButtons={ToolbarButtons}
            loading={isLoadingData}
            growerName={growerName}
            {...otherProps}
            statusColumn={
              !!otherProps.benchmark && otherProps.programIds?.length === 1
                ? statusColumn
                : undefined
            }
            filterInputs={filterInputs}
          />
        </Paper>
      )}

      <Paper sx={{ p: 2 }}>
        <NavButtonsComponent
          traitStatuses={traitStatuses}
          // @ts-ignore
          focusTraitId={focusTraitId}
          onContinue={onContinue}
          onFinishLater={onFinishLater}
          setFocusTraitId={setFocusTraitIdAndOpenDrawer}
        />
      </Paper>
    </Stack>
  )

  return (
    <Grid spacing={4} container>
      <RollupQuestionProvider traitId={focusTraitId} fieldModels={fields || []} years={years}>
        <Grid xs={12} md={2} item>
          <DetailsChecklist
            focusTraitId={focusTraitId}
            traitStatuses={traitStatuses}
            onSelectTrait={setFocusTraitIdAndOpenDrawer}
            isLoading={traitsLoading}
            error={traitsError}
            hideLegend={hideLegend}
          />
        </Grid>
        <Grid md={10} sx={{ display: { xs: 'none', md: 'unset' } }} item>
          {traitEditor}
        </Grid>
        {isMobile && (
          <Drawer
            aria-labelledby="currentTraitId"
            ModalProps={{ onClose: () => setDrawerOpen(false) }}
            anchor="right"
            open={drawerOpen}
            PaperProps={{ style: { width: 'calc(100vw - 40px)' } }}
          >
            <Stack p={1} direction="row" justifyContent="space-between">
              <Typography variant="h5" id="currentTraitId">
                {traitIdT(focusTraitId || 'unknown')}
              </Typography>
              <Button startIcon={<ArrowBackIcon />} onClick={() => setDrawerOpen(false)}>
                {t('Back')}
              </Button>
            </Stack>
            {traitEditor}
          </Drawer>
        )}
      </RollupQuestionProvider>
    </Grid>
  )
}
